import {UI} from "../../../stem-core/ui/UIBase";
import {
    apiRenderEmailTemplate,
    apiSendTestEmailTemplate,
    apiUpdateEmailTemplate, EmailTemplateType, type PreviewEmailTemplateRequest,
} from "../../../client/state/EmailTemplateStore";
import {Button} from "../../../stem-core/ui/button/Button";
import {TextInput} from "../../../stem-core/ui/input/Input";
import {BlinkInputField} from "../../common/Input";
import {CodeEditor} from "../../../stem-core/ui/CodeEditor";
import {StemDate} from "../../../stem-core/time/Date";
import {RawHTML} from "../../../stem-core/ui/RawHTML.jsx";
import {FlatTabArea} from "../../../stem-core/ui/tabs/FlatTabArea";
import {Select} from "../../../stem-core/ui/input/Input";
import {Level} from "../../../stem-core/ui/Constants";
import {DashboardTitle} from "../../common/DashboardTitle";
import {MerchantEmailSender} from "../../../client/state/merchant/MerchantEmailSender";
import {MerchantEmailingSettings} from "../../../client/state/merchant/MerchantEmailingSettingsStore";
import {MultiEmailAddressInput} from "../../common/Input";
import {CycleUnit} from "../../../client/state/RecurringPaymentStoreObject";
import {Currency} from "../../../stem-core/localization/CurrencyStore.js";
import {DashboardMoneyInput} from "../../ui/input/DashboardMoneyInput";
import {CheckboxInput} from "../../../stem-core/ui/input/checkbox/CheckboxInput.jsx";
import type {EmailTemplate} from "../../../client/state/EmailTemplateStore";
import type {UIElement} from "../../../stem-core/ui/UIBase";


function uiRef(targetProto: object, name: string, descriptor: PropertyDescriptor) {
    const rawField = Symbol.for("ref-" + name);

    return {
        get() {
            return this[rawField] || {
                parent: this,
                name: name,
            }
        },
        set(value: unknown) {
            this[rawField] = value;
        }
    }
}


interface EmailTemplatePreviewPanelOptions {
    emailTemplate?: EmailTemplate;
    html?: string;
    parentEditor?: EmailTemplateEditor;
    onShow?: (...args: any[]) => any;
}

class EmailTemplatePreviewPanel extends UI.Element<EmailTemplatePreviewPanelOptions> {
    declare fieldAmount?: any;
    declare fieldFrequency: Select<CycleUnit>;
    declare includeDefaultBCCInput: CheckboxInput;

    addShowListener(callback: (panel: EmailTemplatePreviewPanel) => void) {
        return this.addListener("show", () => callback(this));
    }

    // TODO Try a context pattern here
    redrawPreview() {
        this.options.parentEditor.previewEmailTemplate(this);
    }

    renderSuccessfulDonationContextInputs() {
        const {emailTemplate} = this.options;
        const merchant = emailTemplate.merchant;
        const currency = merchant?.getCurrency() || Currency.get("USD");
        return [
            <BlinkInputField label="Amount">
                <DashboardMoneyInput
                    ref="fieldPreviewAmount"
                    currency={currency}
                    onChange={() => this.redrawPreview()}
                    inputAttributes={{min: "1", max: "1000"}}
                    initialValue={"20"}
                />
            </BlinkInputField>,
            <BlinkInputField label="Frequency">
                <Select
                    ref="fieldFrequency"
                    options={CycleUnit.all()}
                />
            </BlinkInputField>
        ]
    }

    renderSuccessfulSubscriptionContextInputs() {
        // TODO @cleanup this
        return [
            <BlinkInputField label="Subscription tier">
                <Select
                    options={["Sub1", "sub2"]}
                />
            </BlinkInputField>
        ]
    }

    renderContextInputs(emailTemplate: EmailTemplate) {
        if (emailTemplate.type === EmailTemplateType.DONATION_SUCCESS) {
            return this.renderSuccessfulDonationContextInputs();
        }
        if (emailTemplate.type === EmailTemplateType.SUBSCRIPTION_SUCCESS) {
            return this.renderSuccessfulSubscriptionContextInputs();
        }
        return null;
    }

    getExtraContext() {
        // TODO properly implement this
        const amount = this.fieldAmount?.getValue();
        return {
            amount: amount?.getAmount(),
            currencyId: amount?.getCurrency().id,
            frequency: this.fieldFrequency?.getValue(),
        }
    }

    render() {
        const {parentEditor, html, emailTemplate} = this.options;

        return [
            <div style={{marginTop: 10}}>
                <span>
                    {this.renderContextInputs(emailTemplate)}
                </span>
                <span style={{float: "right"}}>
                    <Button onClick={() => parentEditor.sendTestEmail(this.getExtraContext(), this.includeDefaultBCCInput.getValue())}>
                        Send me a test email
                    </Button>
                    <div>
                        <CheckboxInput initialValue={true} ref="includeDefaultBCCInput" label={"Include default BCC"}/>
                    </div>
                </span>
            </div>,
            // TODO: figure out variable height better
            <div style={{height: 600}}>
                {html && <RawHTML innerHTML={html}/>}
            </div>
        ]
    }
}

interface EmailTemplateEditorOptions {
    emailTemplate?: EmailTemplate;
}

export class EmailTemplateEditor extends UI.Element<EmailTemplateEditorOptions> {
    declare previewResponse?: any;
    declare fieldBccEmails: MultiEmailAddressInput;
    declare fieldHTMLEditor: CodeEditor;
    declare fieldReplyTo: TextInput;
    declare fieldSendingAddress: Select<MerchantEmailSender>;
    declare fieldSubject: TextInput;

    blink_default_sending_email = "Blink default"; // TODO: what's this snakecase nonsense?

    getEmailTemplate() {
        return this.options.emailTemplate;
    }

    getMerchant() {
        return this.getEmailTemplate().merchant;
    }

    getDefaultEmailingSettings() {
        const merchant = this.getMerchant();
        return MerchantEmailingSettings.findBy({merchantId: merchant?.id});
    }

    getEmailSenders() {
        const merchant = this.getMerchant();
        return MerchantEmailSender.filterBy({merchantId: merchant?.id});
    }

    // TODO rename
    getEmailPreviewContext() {
        const merchant = this.getMerchant();

        // TODO: if merchant == null, use a default
        return {
            publisher_name: merchant?.name || "Blink Times",
            publisher_url: merchant?.url || "https://dev.blinktimes.com",
            publisher_logo_url: merchant?.wideLogoUrl || "https://cdn.blink.net/news/images/logos/blinkTimes.svg",

            user_dashboard_url: "https://blink.net",
            year: StemDate.format(Date.now(), "YYYY"),

            cycle_duration: "month",
            amount: "$20.00",
            contribution: "$20.00 / month",
            recurring: true,

            code: "434343",
            display_code: "434-343",

            // Useful for preview-ing change email confirmation/undo
            old_email: "john.doe@yahoo.com",
            new_email: "john@doe.net",
            undo_url: "https://example.com",
        }
    }

    getDefaultInputValues() {
        const defaultEmailingSettings = this.getDefaultEmailingSettings();
        const emailTemplate = this.getEmailTemplate();
        const emailSenders = this.getEmailSenders();
        const templateEmailSenderIndex = emailSenders.findIndex(emailSender => emailSender.id === emailTemplate.emailSenderId);

        return {
            fieldSendingAddress: emailSenders ? emailSenders[templateEmailSenderIndex] : null,
            replyTo: emailTemplate.replyTo || defaultEmailingSettings?.emailReplyTo,
            subject: emailTemplate.subject,
            bccEmails: emailTemplate.bccEmails || defaultEmailingSettings?.bccEmails,
            html: emailTemplate.html,
        }
    }

    resetInputs() {
        const defaultInputValues = this.getDefaultInputValues();

        this.fieldSendingAddress.setValue(defaultInputValues.fieldSendingAddress);
        this.fieldReplyTo.setValue(defaultInputValues.replyTo);
        this.fieldSubject.setValue(defaultInputValues.subject);
        this.fieldBccEmails.setValue(defaultInputValues.bccEmails);
        this.fieldHTMLEditor.setValue(defaultInputValues.html);

        // TODO: reset preview as well
    }

    getSelectedEmailSender() {
        return this.fieldSendingAddress?.getValue();
    }

    // TODO: consider implementing this decorator - @wrapInLoadingSpinner
    async saveEmailTemplate() {
        const emailTemplate = this.getEmailTemplate();

        const request = {
            templateId: emailTemplate.id,
            emailSenderId: this.getSelectedEmailSender()?.id,
            replyTo: this.fieldReplyTo.getValue(),
            bccEmails: this.fieldBccEmails.getValue(),
            subject: this.fieldSubject.getValue(),
            html: this.fieldHTMLEditor.getValue(),
            plainTextIsAuto: true, // TODO Only one supported for now
        }

        await apiUpdateEmailTemplate(request);
    }

    getPreviewEmailRequest(baseContext?: any): PreviewEmailTemplateRequest;
    getPreviewEmailRequest(): PreviewEmailTemplateRequest {
        return {
            subject: this.fieldSubject.getValue(),
            html: this.fieldHTMLEditor.getValue(),
            plainText: "",
            context: this.getEmailPreviewContext(),
            emailType: this.getEmailTemplate().type,
        }
    }

    async previewEmailTemplate(previewPanel: EmailTemplatePreviewPanel) {
        const request = this.getPreviewEmailRequest();

        this.previewResponse = await apiRenderEmailTemplate(request);

        previewPanel.updateOptions({html: this.previewResponse.html});
    }

    sendTestEmail(baseContext: Record<string, any>, includeBcc: boolean) {
        const request = {
            ...this.getPreviewEmailRequest(baseContext),
            fromEmail: "support@blink.net", // TODO: wait, we can customize this as well?
            replyTo: this.fieldReplyTo.getValue(),
            bccEmails: includeBcc && this.fieldBccEmails.getValue(),
        }

        apiSendTestEmailTemplate(request);
    }

    render() {
        const defaultInputValues = this.getDefaultInputValues();
        const emailTemplate = this.getEmailTemplate();
        const senderDropdownOptions = this.getEmailSenders();

        return [
            <div style={{padding: 8}}>
                <DashboardTitle style={{display: "inline-block"}} title="Edit email template"/>
                <span style={{float: "right"}}>
                    <Button level={Level.SECONDARY} onClick={() => this.resetInputs()}>
                        Reset changes
                    </Button>
                    <Button onClick={() => this.saveEmailTemplate()}>
                        Save changes
                    </Button>
                </span>
            </div>,
            // I really hate doing this but not giving it a key, all the refs downwards get wrecked
            <FlatTabArea style={{height: 640}} key={Math.random()}>
                <div title="General">
                    <BlinkInputField label="Sending email address">
                        {senderDropdownOptions.length === 0 ?
                            <TextInput disabled={true} placeholder={this.blink_default_sending_email}/> :
                            <Select ref="fieldSendingAddress"
                                    options={senderDropdownOptions}
                                    selected={this.getSelectedEmailSender() || defaultInputValues.fieldSendingAddress}
                            />
                        }
                    </BlinkInputField>
                    <BlinkInputField
                        style={{display: "block"}}
                        label="Reply To"
                        helpInfo="Users will by default reply to this email address."
                    >
                        <TextInput
                            style={{width: 420}}
                            value={defaultInputValues.replyTo}
                            ref={"fieldReplyTo"}
                        />
                    </BlinkInputField>
                    <BlinkInputField
                        style={{display: "block"}}
                        label="Bcc (optional)"
                        helpInfo="A comma separated list of email that will also receive emails sent to users. Can be empty."
                    >
                        <MultiEmailAddressInput
                            style={{width: 420}}
                            value={defaultInputValues.bccEmails}
                            ref={"fieldBccEmails"}
                        />
                    </BlinkInputField>
                    <BlinkInputField
                        style={{display: "block"}}
                        label="Subject"
                    >
                        <TextInput
                            style={{width: 600}}
                            value={emailTemplate.subject}
                            ref={"fieldSubject"}
                        />
                    </BlinkInputField>
                </div>
                <div title="Edit body" style={{height: "100%"}}>
                    <CodeEditor
                        aceMode="django"
                        style={{width: "100%", height: "100%", margin: "auto"}}
                        value={emailTemplate.html}
                        ref={"fieldHTMLEditor"}
                    />
                </div>
                <EmailTemplatePreviewPanel
                    title="Preview"
                    emailTemplate={emailTemplate}
                    parentEditor={this}
                    onShow={(previewPanel) => this.previewEmailTemplate(previewPanel)}
                />
            </FlatTabArea>,
        ]
    }
}
